import pxToRem from "hooks/usePxToRem";

import {Box} from "@mui/material";
import {Grid} from "@mui/system";

import {CustomToggle} from "ui";
import {DefaultCardStructure, ProcessDatePicker} from "components";
import {
  Modality,
  ModalityPayment,
  PlaceIssue,
  TypeKnowledge,
} from "util/globalEnums";
import {useFormContext, Controller} from "react-hook-form";
import ProcessTextField from "views/ProcessDetails/refactor/components/ProcessTextField";
import ProcessSelect from "views/ProcessDetails/refactor/components/ProcessSelect";
import {Process} from "views/ProcessDetails/refactor/types/process.types";
import {getProcessLabel} from "views/ProcessDetails/refactor/utils/processLabels";

const MasterInfo = () => {
  const {control, getValues, setValue} = useFormContext<Process>();

  const indexOfMaster = getValues("knowledges")?.findIndex(
    (knowledge) => knowledge?.typeKnowledge === "Master",
  );

  /**
   * Caso o master não exista, o indexOfMaster será 0.
   */
  const masterIndex = indexOfMaster < 0 ? 0 : indexOfMaster;

  const inputWrapperGridSize = 2;

  return (
    <DefaultCardStructure
      title={getProcessLabel("knowledge.master.title", getValues("modality"))}
      hasDivider={!!getValues(`knowledges.${masterIndex}.directMaster`)}
      isSubcard
    >
      <Grid container rowSpacing={pxToRem(20)} columnSpacing={pxToRem(8)}>
        <Grid size={inputWrapperGridSize}>
          <Controller
            name={`knowledges.${masterIndex}.number`}
            control={control}
            render={({field: {onChange, value}}) => (
              <ProcessTextField
                name={`knowledges.${masterIndex}.number`}
                type="text"
                placeholder={getProcessLabel(
                  "knowledge.master.number",
                  getValues("modality"),
                )}
                value={value ?? ""}
                onChange={(e) => {
                  onChange(e.target.value);

                  setValue(
                    `knowledges.${masterIndex}.typeKnowledge`,
                    TypeKnowledge.Master,
                    {
                      shouldDirty: true,
                    },
                  );
                }}
                size="small"
                fullWidth
                label={getProcessLabel(
                  "knowledge.master.number",
                  getValues("modality"),
                )}
              />
            )}
          />

          {!!getValues(`knowledges.${masterIndex}.directMaster`) && (
            <Box
              sx={{
                marginTop: pxToRem(-4),
                maxHeight: pxToRem(32),
                ml: pxToRem(5),
              }}
            >
              <Controller
                name={`knowledges.${masterIndex}.directMaster`}
                control={control}
                render={({field: {onChange, value}}) => (
                  <CustomToggle
                    name={`knowledges.${masterIndex}.directMaster`}
                    label={getProcessLabel(
                      "knowledge.master.directMaster",
                      getValues("modality"),
                    )}
                    labelPosition="end"
                    onToggleSwitch={onChange}
                    isChecked={!!value}
                    isDisabled
                  />
                )}
              />
            </Box>
          )}
        </Grid>
        <Grid size={inputWrapperGridSize}>
          <Controller
            name={`knowledges.${masterIndex}.dateIssue`}
            control={control}
            render={({field: {value}}) => (
              <ProcessDatePicker
                value={value}
                name={`knowledges.${masterIndex}.dateIssue`}
                title="Data BL"
                fullWidth
                label="Data BL"
                disabled={getValues("modality") === Modality.Aérea}
              />
            )}
          />
        </Grid>
        <Grid size={inputWrapperGridSize}>
          <Controller
            name={`knowledges.${masterIndex}.paymentMethod`}
            control={control}
            render={({field: {onChange, value}}) => (
              <ProcessSelect
                name={`knowledges.${masterIndex}.paymentMethod`}
                value={value ?? ""}
                onChange={onChange}
                data={Object.values(ModalityPayment)}
                size="small"
                disabled
                fullWidth
                label="Modelo De Pagamento"
              />
            )}
          />
        </Grid>
        <Grid size={inputWrapperGridSize}>
          <Controller
            name={`knowledges.${masterIndex}.deconsolidationNumber`}
            control={control}
            render={({field: {onChange, value}}) => (
              <ProcessTextField
                name={`knowledges.${masterIndex}.deconsolidationNumber`}
                value={value ?? ""}
                placeholder="CE n°"
                onChange={onChange}
                size="small"
                disabled
                fullWidth
                label="CE n°"
              />
            )}
          />
        </Grid>
        <Grid size={inputWrapperGridSize}>
          <Controller
            name={`knowledges.${masterIndex}.dateDeconsolidation`}
            control={control}
            render={({field: {value}}) => (
              <ProcessDatePicker
                value={value}
                name={`knowledges.${masterIndex}.dateDeconsolidation`}
                title="Data CE"
                fullWidth
                label="Data CE"
                disabled={getValues("modality") === Modality.Aérea}
              />
            )}
          />
        </Grid>
        <Grid size={inputWrapperGridSize}>
          <Controller
            name={`knowledges.${masterIndex}.placeIssue` as const}
            control={control}
            render={({field: {onChange, value}}) => (
              <ProcessSelect
                name={`knowledges.${masterIndex}.placeIssue`}
                value={value}
                onChange={onChange}
                data={Object.values(PlaceIssue)}
                size="small"
                fullWidth
                label="Local Emissão"
                disabled={getValues("modality") === Modality.Aérea}
              />
            )}
          />
        </Grid>
        {!getValues(`knowledges.${masterIndex}.directMaster`) && (
          <Grid size={inputWrapperGridSize} ml={pxToRem(5)}>
            <Controller
              name={`knowledges.${masterIndex}.directMaster`}
              control={control}
              render={({field: {onChange, value}}) => (
                <CustomToggle
                  name={`knowledges.${masterIndex}.directMaster`}
                  label="BL Direto"
                  labelPosition="end"
                  isDisabled
                  onToggleSwitch={onChange}
                  isChecked={!!value}
                />
              )}
            />
          </Grid>
        )}
      </Grid>
      {!!getValues(`knowledges.${masterIndex}.directMaster`) && (
        <Grid size={12} sx={{marginTop: pxToRem(8)}}>
          <Controller
            name={
              `knowledges.${masterIndex}.logisticBillLading.descriptionOfPackages` as const
            }
            control={control}
            render={({field: {value, onChange}}) => (
              <ProcessTextField
                multiline
                minRows={4}
                maxRows={8}
                size="small"
                fullWidth
                label="Descrição dos pacotes"
                name={
                  `knowledges.${masterIndex}.logisticBillLading.descriptionOfPackages` as const
                }
                value={value ?? ""}
                placeholder="descrição"
                onChange={onChange}
                disabled={getValues("modality") === Modality.Aérea}
              />
            )}
          />
        </Grid>
      )}
    </DefaultCardStructure>
  );
};

export default MasterInfo;
